import type { LineageNode } from "@auradata/contracts";
import {
  Card,
  MetaChip,
  PageHeader,
  SearchInput,
  SegmentedControl,
  StatusText,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
  Toolbar,
} from "@auradata/ui";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { lineageQuery, projectQuery } from "@/lib/api/queries";
import { useOpenModel } from "../lineage/nav";

type Kind = "all" | "model" | "seed" | "snapshot";

const statusOf = (n: LineageNode) =>
  n.status === "fail"
    ? ("err" as const)
    : n.status === "warn"
      ? ("warn" as const)
      : n.status === "ok"
        ? ("ok" as const)
        : ("neutral" as const);

const statusLabel: Record<string, string> = {
  err: "Failed",
  warn: "Built, test warning",
  ok: "Built, tests pass",
  neutral: "Skipped",
};

/**
 * Every node dbt builds in this project, as built by the last finished run in the environment:
 * the same facts the lineage graph shows, as a table. Sources have their own screen.
 */
export function ModelsPage({
  workspace,
  project,
  env,
}: {
  workspace: string;
  project: string;
  env: string;
}) {
  const { data } = useSuspenseQuery(lineageQuery(workspace, project, env));
  const { data: detail } = useSuspenseQuery(projectQuery(workspace, project));
  const openModel = useOpenModel(workspace, project);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<Kind>("all");
  const envName = detail.environments.find((e) => e.slug === env)?.name ?? env;
  const needle = q.trim().toLowerCase();
  const rows = data.nodes
    .filter((n) => n.kind !== "source")
    .filter((n) => kind === "all" || n.kind === kind)
    .filter((n) => !needle || n.id.toLowerCase().includes(needle) || n.folder.includes(needle))
    .sort((a, b) => a.folder.localeCompare(b.folder) || a.id.localeCompare(b.id));
  const count = (k: Kind) =>
    data.nodes.filter((n) => n.kind !== "source" && (k === "all" || n.kind === k)).length;
  return (
    <>
      <PageHeader
        title="Models"
        meta={
          <span className="text-fg-subtle">
            {count("all")} nodes · {count("model")} models · {count("seed")} seeds ·{" "}
            {data.lastRun
              ? `as built by run #${data.lastRun.number}, ${data.lastRun.when}`
              : `no finished run in ${envName} yet`}
          </span>
        }
      />
      <Card>
        <Toolbar>
          <SearchInput
            value={q}
            onValueChange={setQ}
            placeholder="Filter by model or folder…"
            aria-label="Filter models"
            className="w-[260px]"
          />
          <SegmentedControl<Kind>
            aria-label="Kind"
            value={kind}
            onValueChange={setKind}
            options={[
              { value: "all", label: "All", count: count("all") },
              { value: "model", label: "Models", count: count("model") },
              { value: "seed", label: "Seeds", count: count("seed") },
              { value: "snapshot", label: "Snapshots", count: count("snapshot") },
            ]}
          />
          <span className="flex-1" />
          <span className="text-xs text-fg-subtle">{envName}</span>
        </Toolbar>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Model</TableHead>
              <TableHead className="w-[140px]">Materialization</TableHead>
              <TableHead className="w-[160px]">Folder</TableHead>
              <TableHead className="w-[180px]">Last result</TableHead>
              <TableHead className="w-[100px]">Rows</TableHead>
              <TableHead className="w-[140px]">Tests</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableEmpty colSpan={6}>No models match this filter.</TableEmpty>
            ) : null}
            {rows.map((n) => {
              const s = statusOf(n);
              return (
                <TableRow
                  key={n.id}
                  clickable
                  tabIndex={0}
                  onClick={() => openModel(n.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") openModel(n.id);
                  }}
                >
                  <TableCell mono>{n.id}</TableCell>
                  <TableCell>
                    <MetaChip className="h-5 px-2 text-2xs">{n.materialization}</MetaChip>
                  </TableCell>
                  <TableCell mono muted>
                    {n.folder || "—"}
                  </TableCell>
                  <TableCell>
                    <StatusText status={s}>{statusLabel[s]}</StatusText>
                  </TableCell>
                  <TableCell mono muted>
                    {n.rows === null ? "—" : n.rows.toLocaleString()}
                  </TableCell>
                  <TableCell muted>
                    {n.hasTests
                      ? `${n.tests.passed} passed${n.tests.warned ? ` · ${n.tests.warned} warn` : ""}${n.tests.failed ? ` · ${n.tests.failed} failed` : ""}`
                      : "no tests"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}
